import React, { useState } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  DatePicker,
  Select,
  Space,
  Typography,
  Table,
  Progress,
  Tag,
  Spin,
  Alert,
  Button,
  Tooltip,
  theme,
} from 'antd';
import {
  TruckOutlined,
  TrophyOutlined,
  ShopOutlined,
  CalendarOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
  Area,
  AreaChart,
  Tooltip as RechartsTooltip,
} from 'recharts';
import { usePartnerStatistics } from '../hooks/usePartnerStatistics';
import { GetPartnerStatisticsInput } from '../types/partnerStatistics';
import { formatSAR, formatNumber } from '../common/currency';
import styled from 'styled-components';
import moment from 'moment';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title, Text } = Typography;

interface ThemeProps {
  $isDark?: boolean;
  $bgColor?: string;
  $textColor?: string;
  $borderColor?: string;
}

const DashboardContainer = styled.div<ThemeProps>`
  padding: 24px;
  background: ${props => props.$isDark 
    ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' 
    : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'};
  min-height: 100vh;
`;

const StatsCard = styled(Card)<ThemeProps>`
  border-radius: 12px;
  box-shadow: ${props => props.$isDark 
    ? '0 4px 12px rgba(0, 0, 0, 0.3)' 
    : '0 4px 12px rgba(0, 0, 0, 0.1)'};
  border: none;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.$isDark 
      ? '0 8px 24px rgba(0, 0, 0, 0.4)' 
      : '0 8px 24px rgba(0, 0, 0, 0.15)'};
  }
  
  .ant-card-body {
    padding: 24px;
  }
  
  .ant-statistic-title {
    color: ${props => props.$isDark ? '#a0aec0' : '#666'};
    font-weight: 500;
    margin-bottom: 8px;
    font-size: 14px;
  }
  
  .ant-statistic-content {
    color: #1890ff;
  }
  
  &.earnings {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    
    .ant-statistic-title,
    .ant-statistic-content {
      color: white !important;
    }
  }
  
  &.deliveries {
    background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
    color: white;
    
    .ant-statistic-title,
    .ant-statistic-content {
      color: white !important;
    }
  }
  
  &.completion {
    background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
    color: white;
    
    .ant-statistic-title,
    .ant-statistic-content {
      color: white !important;
    }
  }
  
  &.cancelled {
    background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
    color: white;
    
    .ant-statistic-title,
    .ant-statistic-content {
      color: white !important;
    }
  }
`;

const FilterCard = styled(Card)<ThemeProps>`
  margin-bottom: 24px;
  border-radius: 12px;
  box-shadow: ${props => props.$isDark 
    ? '0 2px 8px rgba(0, 0, 0, 0.3)' 
    : '0 2px 8px rgba(0, 0, 0, 0.1)'};
  border: none;
  background: ${props => props.$bgColor || 'white'};
  
  .ant-card-body {
    padding: 20px 24px;
  }
`;

const ChartCard = styled(Card)<ThemeProps>`
  border-radius: 12px;
  box-shadow: ${props => props.$isDark 
    ? '0 4px 12px rgba(0, 0, 0, 0.3)' 
    : '0 4px 12px rgba(0, 0, 0, 0.1)'};
  border: none;
  background: ${props => props.$bgColor || 'white'};
  
  .ant-card-head {
    border-bottom: 1px solid ${props => props.$borderColor || '#f0f0f0'};
    border-radius: 12px 12px 0 0;
    background: ${props => props.$bgColor || 'white'};
    color: ${props => props.$textColor || 'inherit'};
  }
  
  .ant-card-head-title {
    color: ${props => props.$textColor || 'inherit'};
  }
  
  .ant-card-body {
    padding: 24px;
    background: ${props => props.$bgColor || 'white'};
  }
`;

const MetricCard = styled.div<ThemeProps>`
  background: ${props => props.$bgColor || 'white'};
  border-radius: 8px;
  padding: 16px;
  box-shadow: ${props => props.$isDark 
    ? '0 2px 8px rgba(0, 0, 0, 0.3)' 
    : '0 2px 8px rgba(0, 0, 0, 0.1)'};
  border-left: 4px solid #1890ff;
  
  &.success {
    border-left-color: #52c41a;
  }
  
  &.warning {
    border-left-color: #fa8c16;
  }
  
  &.error {
    border-left-color: #f5222d;
  }
  
  .ant-statistic-title {
    color: ${props => props.$isDark ? '#a0aec0' : '#666'};
  }
`;


interface EnhancedPartnerStatisticsDashboardProps {
  className?: string;
}

export const EnhancedPartnerStatisticsDashboard: React.FC<EnhancedPartnerStatisticsDashboardProps> = ({ className }) => {
  const [filters, setFilters] = useState<GetPartnerStatisticsInput>({
    startDate: null,
    endDate: null,
    partnerId: null,
    storeId: null,
  });

  const { data, loading, error, refetch } = usePartnerStatistics(filters);
  
  // Theme hooks
  const { token } = theme.useToken();
  const isDark = Boolean(
    token.colorBgContainer === '#141414' || 
    token.colorBgBase === '#000000' || 
    (token.colorBgContainer && parseInt(token.colorBgContainer.replace('#', ''), 16) < 0x808080)
  );
  
  // Theme-aware colors
  const themeColors = {
    bgColor: token.colorBgContainer,
    textColor: token.colorText,
    borderColor: token.colorBorder,
    secondaryText: token.colorTextSecondary,
    chartGridColor: isDark ? '#404040' : '#e0e0e0',
    chartTextColor: isDark ? '#a0aec0' : '#666',
  };

  const handleDateRangeChange = (dates: any) => {
    setFilters(prev => ({
      ...prev,
      startDate: dates?.[0]?.format('YYYY-MM-DD') || null,
      endDate: dates?.[1]?.format('YYYY-MM-DD') || null,
    }));
  };

  const handlePartnerChange = (partnerId: number | null) => {
    setFilters(prev => ({ ...prev, partnerId }));
  };

  const handleStoreChange = (storeId: number | null) => {
    setFilters(prev => ({ ...prev, storeId }));
  };

  const handleRefresh = () => {
    refetch();
  };

  const statistics = data?.getPartnerStatistics;

  // Prepare chart data
  const trendData = statistics?.dailyTrend?.map(trend => ({
    date: moment(trend.date).format('MMM DD'),
    deliveries: trend.deliveryCount,
    earnings: trend.totalEarnings,
  })) || [];

  const partnersData = statistics?.topPartners?.slice(0, 5).map(partner => ({
    name: partner.partnerName.length > 15 ? partner.partnerName.substring(0, 15) + '...' : partner.partnerName,
    earnings: partner.totalEarnings,
    deliveries: partner.totalDeliveries,
    completionRate: partner.completionRate * 100,
  })) || [];

  const storesData = statistics?.topStoresByPartner?.slice(0, 5).map(store => ({
    name: store.storeName.length > 15 ? store.storeName.substring(0, 15) + '...' : store.storeName,
    revenue: store.totalRevenue,
    orders: store.orderCount,
  })) || [];

  const deliveryStatusData = [
    { name: 'Completed', value: statistics?.completedDeliveries || 0, color: '#52c41a' },
    { name: 'Cancelled', value: statistics?.cancelledDeliveries || 0, color: '#f5222d' },
  ];

  const topPartnersColumns = [
    {
      title: 'Rank',
      key: 'rank',
      width: 60,
      render: (_: any, __: any, index: number) => (
        <div style={{ textAlign: 'center' }}>
          <Tag color={index < 3 ? 'gold' : 'default'}>
            #{index + 1}
          </Tag>
        </div>
      ),
    },
    {
      title: 'Partner Name',
      dataIndex: 'partnerName',
      key: 'partnerName',
      render: (name: string) => (
        <Text strong>{name}</Text>
      ),
    },
    {
      title: 'Total Deliveries',
      dataIndex: 'totalDeliveries',
      key: 'totalDeliveries',
      render: (value: number) => (
        <Tag color="blue">{formatNumber(value)}</Tag>
      ),
    },
    {
      title: 'Completion Rate',
      dataIndex: 'completionRate',
      key: 'completionRate',
      render: (rate: number) => (
        <Tooltip title={`${(rate * 100).toFixed(1)}%`}>
          <Progress 
            percent={Math.round(rate * 100)} 
            size="small" 
            status={rate > 0.8 ? 'success' : rate > 0.6 ? 'normal' : 'exception'}
            strokeColor={rate > 0.8 ? '#52c41a' : rate > 0.6 ? '#1890ff' : '#f5222d'}
          />
        </Tooltip>
      ),
    },
    {
      title: 'Total Earnings',
      dataIndex: 'totalEarnings',
      key: 'totalEarnings',
      render: (value: number) => (
        <Text strong style={{ color: '#52c41a' }}>
          {formatSAR(value)}
        </Text>
      ),
    },
    {
      title: 'Avg. per Delivery',
      dataIndex: 'averageEarningsPerDelivery',
      key: 'averageEarningsPerDelivery',
      render: (value: number) => (
        <Text>${value.toFixed(2)}</Text>
      ),
    },
  ];

  const topStoresColumns = [
    {
      title: 'Rank',
      key: 'rank',
      width: 60,
      render: (_: any, __: any, index: number) => (
        <div style={{ textAlign: 'center' }}>
          <Tag color={index < 3 ? 'gold' : 'default'}>
            #{index + 1}
          </Tag>
        </div>
      ),
    },
    {
      title: 'Store Name',
      dataIndex: 'storeName',
      key: 'storeName',
      render: (name: string, record: any) => (
        <div>
          <Text strong>{name}</Text>
          {record.storeNameAr && (
            <div>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {record.storeNameAr}
              </Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Order Count',
      dataIndex: 'orderCount',
      key: 'orderCount',
      render: (value: number) => (
        <Tag color="purple">{formatNumber(value)}</Tag>
      ),
    },
    {
      title: 'Total Revenue',
      dataIndex: 'totalRevenue',
      key: 'totalRevenue',
      render: (value: number) => (
        <Text strong style={{ color: '#52c41a' }}>
          {formatSAR(value)}
        </Text>
      ),
    },
    {
      title: 'Avg. Order Value',
      dataIndex: 'averageOrderValue',
      key: 'averageOrderValue',
      render: (value: number) => (
        <Text>${value.toFixed(2)}</Text>
      ),
    },
  ];

  if (error) {
    return (
      <DashboardContainer className={className} $isDark={isDark}>
        <Alert
          message="Error Loading Partner Statistics"
          description={error.message}
          type="error"
          showIcon
          action={
            <Button size="small" danger onClick={handleRefresh}>
              Try Again
            </Button>
          }
        />
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer className={className} $isDark={isDark}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0, color: token.colorPrimary, textAlign: 'center' }}>
          🚀 Partner Statistics Dashboard
        </Title>
        <Text type="secondary" style={{ display: 'block', textAlign: 'center', fontSize: '16px' }}>
          Comprehensive analytics and insights for partner performance
        </Text>
      </div>

      {/* Filters */}
      <FilterCard $isDark={isDark} $bgColor={themeColors.bgColor}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={6}>
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Text strong>📅 Date Range</Text>
              <RangePicker
                style={{ width: '100%' }}
                onChange={handleDateRangeChange}
                placeholder={['Start Date', 'End Date']}
              />
            </Space>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Text strong>👥 Partner</Text>
              <Select
                style={{ width: '100%' }}
                placeholder="Select Partner"
                allowClear
                onChange={handlePartnerChange}
              >
                <Option value={1}>Partner 1</Option>
                <Option value={2}>Partner 2</Option>
              </Select>
            </Space>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Text strong>🏪 Store</Text>
              <Select
                style={{ width: '100%' }}
                placeholder="Select Store"
                allowClear
                onChange={handleStoreChange}
              >
                <Option value={1}>Store 1</Option>
                <Option value={2}>Store 2</Option>
              </Select>
            </Space>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={loading}
              style={{ marginTop: 24, width: '100%' }}
              size="large"
            >
              Refresh Data
            </Button>
          </Col>
        </Row>
      </FilterCard>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">Loading partner statistics...</Text>
          </div>
        </div>
      ) : (
        <>
          {/* Key Metrics */}
          <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} lg={6}>
              <StatsCard className="deliveries" $isDark={isDark}>
                <Statistic
                  title="🚚 Total Deliveries"
                  value={statistics?.totalDeliveries || 0}
                  valueStyle={{ color: 'white', fontSize: '28px' }}
                />
              </StatsCard>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <StatsCard className="earnings" $isDark={isDark}>
                <Statistic
                  title="💰 Total Earnings"
                  value={statistics?.totalEarnings || 0}
                  prefix="$"
                  precision={2}
                  valueStyle={{ color: 'white', fontSize: '28px' }}
                />
              </StatsCard>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <StatsCard className="completion" $isDark={isDark}>
                <Statistic
                  title="✅ Completion Rate"
                  value={((statistics?.completionRate || 0) * 100)}
                  suffix="%"
                  precision={1}
                  valueStyle={{ color: 'white', fontSize: '28px' }}
                />
              </StatsCard>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <StatsCard className="cancelled" $isDark={isDark}>
                <Statistic
                  title="❌ Cancelled Deliveries"
                  value={statistics?.cancelledDeliveries || 0}
                  valueStyle={{ color: 'white', fontSize: '28px' }}
                />
              </StatsCard>
            </Col>
          </Row>

          {/* Charts Row */}
          <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
            {/* Daily Trend Chart */}
            <Col xs={24} lg={12}>
              <ChartCard
                $isDark={isDark}
                $bgColor={themeColors.bgColor}
                $textColor={themeColors.textColor}
                $borderColor={themeColors.borderColor}
                title={
                  <Space>
                    <CalendarOutlined />
                    📈 Daily Earnings Trend
                  </Space>
                }
              >
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={themeColors.chartGridColor} />
                    <XAxis dataKey="date" tick={{ fill: themeColors.chartTextColor }} stroke={themeColors.chartGridColor} />
                    <YAxis tick={{ fill: themeColors.chartTextColor }} stroke={themeColors.chartGridColor} />
                    <RechartsTooltip 
                      contentStyle={{ 
                        backgroundColor: themeColors.bgColor, 
                        border: `1px solid ${themeColors.borderColor}`,
                        color: themeColors.textColor 
                      }} 
                    />
                    <Area 
                      type="monotone" 
                      dataKey="earnings" 
                      stroke="#1890ff" 
                      fill="url(#colorEarnings)" 
                    />
                    <defs>
                      <linearGradient id="colorEarnings" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#1890ff" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#1890ff" stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                  </AreaChart>
                </ResponsiveContainer>
              </ChartCard>
            </Col>

            {/* Delivery Status Pie Chart */}
            <Col xs={24} lg={12}>
              <ChartCard
                $isDark={isDark}
                $bgColor={themeColors.bgColor}
                $textColor={themeColors.textColor}
                $borderColor={themeColors.borderColor}
                title={
                  <Space>
                    <TruckOutlined />
                    📊 Delivery Status Distribution
                  </Space>
                }
              >
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={deliveryStatusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {deliveryStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip 
                      contentStyle={{ 
                        backgroundColor: themeColors.bgColor, 
                        border: `1px solid ${themeColors.borderColor}`,
                        color: themeColors.textColor 
                      }} 
                    />
                    <Legend wrapperStyle={{ color: themeColors.textColor }} />
                  </PieChart>
                </ResponsiveContainer>
              </ChartCard>
            </Col>
          </Row>

          {/* Performance Charts */}
          <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
            {/* Top Partners Chart */}
            <Col xs={24} lg={12}>
              <ChartCard
                $isDark={isDark}
                $bgColor={themeColors.bgColor}
                $textColor={themeColors.textColor}
                $borderColor={themeColors.borderColor}
                title={
                  <Space>
                    <TrophyOutlined />
                    🏆 Top Partners Performance
                  </Space>
                }
              >
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={partnersData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={themeColors.chartGridColor} />
                    <XAxis dataKey="name" tick={{ fill: themeColors.chartTextColor }} stroke={themeColors.chartGridColor} />
                    <YAxis tick={{ fill: themeColors.chartTextColor }} stroke={themeColors.chartGridColor} />
                    <RechartsTooltip 
                      contentStyle={{ 
                        backgroundColor: themeColors.bgColor, 
                        border: `1px solid ${themeColors.borderColor}`,
                        color: themeColors.textColor 
                      }} 
                    />
                    <Bar dataKey="earnings" fill="#52c41a" name="Earnings ($)" />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </Col>

            {/* Top Stores Chart */}
            <Col xs={24} lg={12}>
              <ChartCard
                $isDark={isDark}
                $bgColor={themeColors.bgColor}
                $textColor={themeColors.textColor}
                $borderColor={themeColors.borderColor}
                title={
                  <Space>
                    <ShopOutlined />
                    🏪 Top Stores Revenue
                  </Space>
                }
              >
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={storesData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={themeColors.chartGridColor} />
                    <XAxis dataKey="name" tick={{ fill: themeColors.chartTextColor }} stroke={themeColors.chartGridColor} />
                    <YAxis tick={{ fill: themeColors.chartTextColor }} stroke={themeColors.chartGridColor} />
                    <RechartsTooltip 
                      contentStyle={{ 
                        backgroundColor: themeColors.bgColor, 
                        border: `1px solid ${themeColors.borderColor}`,
                        color: themeColors.textColor 
                      }} 
                    />
                    <Bar dataKey="revenue" fill="#1890ff" name="Revenue ($)" />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </Col>
          </Row>

          {/* Additional Metrics */}
          <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={8}>
              <MetricCard className="success" $isDark={isDark} $bgColor={themeColors.bgColor}>
                <Statistic
                  title="✅ Completed Deliveries"
                  value={statistics?.completedDeliveries || 0}
                  valueStyle={{ color: '#52c41a', fontSize: '24px' }}
                />
              </MetricCard>
            </Col>
            <Col xs={24} sm={8}>
              <MetricCard className="warning" $isDark={isDark} $bgColor={themeColors.bgColor}>
                <Statistic
                  title="🚚 Total Delivery Fees"
                  value={statistics?.totalDeliveryFees || 0}
                  prefix="$"
                  precision={2}
                  valueStyle={{ color: '#fa8c16', fontSize: '24px' }}
                />
              </MetricCard>
            </Col>
            <Col xs={24} sm={8}>
              <MetricCard $isDark={isDark} $bgColor={themeColors.bgColor}>
                <Statistic
                  title="💡 Total Tips"
                  value={statistics?.totalTips || 0}
                  prefix="$"
                  precision={2}
                  valueStyle={{ color: '#13c2c2', fontSize: '24px' }}
                />
              </MetricCard>
            </Col>
          </Row>

          {/* Top Partners Table */}
          <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
            <Col span={24}>
              <ChartCard
                $isDark={isDark}
                $bgColor={themeColors.bgColor}
                $textColor={themeColors.textColor}
                $borderColor={themeColors.borderColor}
                title={
                  <Space>
                    <TrophyOutlined />
                    🏆 Top Performing Partners
                    <Tooltip title="Ranked by total earnings and performance metrics">
                      <InfoCircleOutlined style={{ color: token.colorPrimary }} />
                    </Tooltip>
                  </Space>
                }
              >
                <Table
                  columns={topPartnersColumns}
                  dataSource={statistics?.topPartners || []}
                  rowKey="partnerId"
                  pagination={{ pageSize: 5, showSizeChanger: false }}
                  scroll={{ x: 800 }}
                />
              </ChartCard>
            </Col>
          </Row>

          {/* Top Stores Table */}
          <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
            <Col span={24}>
              <ChartCard
                $isDark={isDark}
                $bgColor={themeColors.bgColor}
                $textColor={themeColors.textColor}
                $borderColor={themeColors.borderColor}
                title={
                  <Space>
                    <ShopOutlined />
                    🏪 Top Stores by Partner
                    <Tooltip title="Ranked by total revenue and order volume">
                      <InfoCircleOutlined style={{ color: token.colorPrimary }} />
                    </Tooltip>
                  </Space>
                }
              >
                <Table
                  columns={topStoresColumns}
                  dataSource={statistics?.topStoresByPartner || []}
                  rowKey="storeId"
                  pagination={{ pageSize: 5, showSizeChanger: false }}
                  scroll={{ x: 800 }}
                />
              </ChartCard>
            </Col>
          </Row>
        </>
      )}
    </DashboardContainer>
  );
};
